import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close,
  Star,
  Schedule,
  CheckCircle,
} from '@mui/icons-material';
import { ReviewSubmissionForm } from './ReviewSubmissionForm';
import { ReviewEligibilityService } from '@/utils/review-eligibility-service';
import { ReviewEligibilityResponse, ReviewSubmissionData } from '@/types/review-types';
import { Booking } from '@/types';

interface ReviewPromptModalProps {
  open: boolean;
  onClose: () => void;
  booking: Booking | null;
  onReviewSubmitted?: () => void;
}

export const ReviewPromptModal: React.FC<ReviewPromptModalProps> = ({
  open,
  onClose,
  booking,
  onReviewSubmitted,
}) => {
  const [eligibility, setEligibility] = useState<ReviewEligibilityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);


  const checkEligibility = useCallback(async () => {
    if (!booking) return;

    setLoading(true);
    setError(null);

    try {
      const eligibilityData = await ReviewEligibilityService.checkReviewEligibility(booking.bookingId);
      setEligibility(eligibilityData);
    } catch (err) {
      console.error('Error checking review eligibility:', err);
      setError(err instanceof Error ? err.message : 'Failed to check review eligibility');
    } finally {
      setLoading(false);
    }
  }, [booking]);
  useEffect(() => {
    if (open && booking) {
      checkEligibility();
    }
  }, [open, booking, checkEligibility]);

  const handleStartReview = () => {
    setShowReviewForm(true);
  };

  const handleReviewSubmit = async (reviewData: ReviewSubmissionData) => {
    if (!booking || !eligibility) return;

    setSubmitting(true);
    try {
      // The ReviewSubmissionForm will handle the actual submission
      // This is just for the modal state management
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
      onClose();
    } catch (err) {
      console.error('Error in review submission flow:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReviewCancel = () => {
    setShowReviewForm(false);
  };

  const handleClose = () => {
    setShowReviewForm(false);
    onClose();
  };

  if (!booking) return null;

  const daysRemaining = eligibility ? ReviewEligibilityService.calculateDaysRemaining(eligibility.deadline) : 0;
  const deadlineFormatted = eligibility ? ReviewEligibilityService.formatDeadline(eligibility.deadline) : '';

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" component="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Star color="primary" />
            {showReviewForm ? 'Write Your Review' : 'Share Your Experience'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Checking review eligibility...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : !eligibility?.canReview ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {eligibility?.existingReview ? 'Review Already Submitted' : 'Review Not Available'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {eligibility?.existingReview 
                ? 'You have already submitted a review for this booking.'
                : 'This booking is not eligible for review at this time.'
              }
            </Typography>
          </Box>
        ) : showReviewForm ? (
          <ReviewSubmissionForm
            bookingId={booking.bookingId}
            reviewerType={eligibility.reviewType || 'guest'}
            revieweeId={eligibility.reviewType === 'guest' ? booking.listingId : booking.guestId}
            listingId={eligibility.reviewType === 'guest' ? booking.listingId : undefined}
            onSubmit={handleReviewSubmit}
            onCancel={handleReviewCancel}
            loading={submitting}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Booking Summary */}
            <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Your Recent Stay
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Booking #{booking.bookingId.slice(-8)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date(booking.startDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })} - {new Date(booking.endDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Typography>
            </Box>

            {/* Review Prompt */}
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Star sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                How was your experience?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {eligibility.reviewType === 'guest' 
                  ? 'Share your thoughts about the property and your host to help future guests.'
                  : 'Share your experience with this guest to help other hosts.'
                }
              </Typography>

              {/* Deadline Information */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Chip
                  icon={<Schedule />}
                  label={`${daysRemaining} days remaining`}
                  color={daysRemaining <= 3 ? 'warning' : 'info'}
                  variant="outlined"
                />
              </Box>

              <Typography variant="body2" color="text.secondary">
                Review deadline: {deadlineFormatted}
              </Typography>
            </Box>

            {/* Benefits of Reviewing */}
            <Box sx={{ p: 3, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary.main">
                Why review?
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                <Typography component="li" variant="body2" color="text.secondary">
                  Help other {eligibility.reviewType === 'guest' ? 'travelers' : 'hosts'} make informed decisions
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Provide valuable feedback to improve the experience
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Build trust within our community
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      {!loading && !error && eligibility?.canReview && !showReviewForm && (
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button onClick={handleClose} variant="outlined" fullWidth>
            Maybe Later
          </Button>
          <Button onClick={handleStartReview} variant="contained" fullWidth>
            Write Review
          </Button>
        </DialogActions>
      )}

      {(!eligibility?.canReview || error) && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} variant="contained" fullWidth>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ReviewPromptModal;